import type { ConversationMemberRef, MentionKind } from '@cyphex/types';

/**
 * WHO AN `@…` MAY MEAN in the chat being written in.
 *
 * The chat composer is a plain textarea, so a mention is TEXT - "@Maya Patel" -
 * until the moment of sending, when it is matched against the people actually
 * in the conversation. Matching by name at send time, rather than remembering
 * what the picker inserted, means a draft restored after a reload, a name typed
 * by hand and a message being edited all resolve the same way.
 *
 * Before this existed every `@word` became a chip with NO user id. The server
 * discards an id-less mention, so the chip was a promise nobody kept: no
 * "mentioned you", no @ badge, and nothing that could get through a mute.
 */
export interface MentionTarget {
  kind: Extract<MentionKind, 'user' | 'all'>;
  /** The person's id. `null` for `@everyone`. */
  id: string | null;
  /** What follows the `@`, and what the chip shows. */
  label: string;
  /** Also accepted after the `@` - the username - for the same person. */
  alias?: string;
  /**
   * Somebody else here has the SAME NAME. "@Maya Patel" then means neither of
   * them - only the username tells them apart, so that is what the picker
   * writes and the only spelling that resolves. (Matched by name, the second
   * Maya could be picked from the list and the first one was the one told.)
   */
  shared?: boolean;
  avatarUrl?: string | null;
  /** Second line in the picker. */
  hint?: string;
}

/**
 * A character that continues a name, so "@Maya" does not match inside
 * "@Mayank". MARKS COUNT: in Devanagari, Tamil, Thai and their relatives a
 * vowel sign is a combining mark, not a letter, and without `\p{M}` a name
 * ended wherever the first one appeared - "@राम" matched inside "@रामेश्वर".
 */
const NAME_CHAR = /[\p{L}\p{N}\p{M}_]/u;
/** A character that makes the `@` part of an address rather than the start of a mention. */
const ADDRESS_CHAR = /[A-Za-z0-9._%+-]/;

/** What may follow the `@` for this person. */
function namesOf(target: MentionTarget): string[] {
  if (target.shared === true) return target.alias === undefined ? [] : [target.alias];
  return target.alias === undefined ? [target.label] : [target.label, target.alias];
}

/** What the picker writes after the `@`: the one spelling that means this person only. */
export function mentionText(target: MentionTarget): string {
  return target.shared === true && target.alias !== undefined ? target.alias : target.label;
}

/**
 * The target named at `source[at] === '@'`, and how many characters the mention
 * spans, `@` included. The LONGEST name wins, so "@Maya Patel" is not cut down
 * to "@Maya" when both people are in the chat.
 */
export function mentionAt(
  source: string,
  at: number,
  targets: readonly MentionTarget[],
): { target: MentionTarget; length: number } | null {
  let best: { target: MentionTarget; length: number } | null = null;
  for (const target of targets) {
    for (const name of namesOf(target)) {
      if (name.length === 0) continue;
      const end = at + 1 + name.length;
      if (source.slice(at + 1, end).toLowerCase() !== name.toLowerCase()) continue;
      const next = source[end];
      if (next !== undefined && NAME_CHAR.test(next)) continue;
      if (best === null || name.length + 1 > best.length) best = { target, length: name.length + 1 };
    }
  }
  return best;
}

const MAX_QUERY = 40;
/** "first middle last" is a name; a sentence is not. */
const MAX_SPACES = 3;

/**
 * The `@query` being typed immediately before the caret, or null.
 *
 * `start` is the index of the `@`. An `@` glued to an address character
 * ("sai@wmlit.com") never opens the picker.
 */
export function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const head = text.slice(0, caret);
  const start = head.lastIndexOf('@');
  if (start < 0) return null;
  const before = start === 0 ? '' : (head[start - 1] ?? '');
  if (before !== '' && ADDRESS_CHAR.test(before)) return null;
  const query = head.slice(start + 1);
  if (query.length > MAX_QUERY || query.includes('\n') || query.startsWith(' ')) return null;
  if ((query.match(/ /g) ?? []).length > MAX_SPACES) return null;
  return { start, query };
}

/**
 * `mentionQueryAt`, minus the case that is not a question: the caret sitting
 * INSIDE a name that is already written ("@Maya |Patel, the notes"). That is
 * somebody moving through their own text. Opening the list there took the
 * arrow keys away from them, and Enter wrote the rest of the name a second time.
 *
 * ...AND THE ONE THAT IS ALREADY ANSWERED: "@Ravi |" - a whole name, and the
 * space that ends it. With a "Ravi Kumar" in the chat that still matched him,
 * so picking Ravi brought the list straight back, and the next Enter - meant
 * to SEND - rewrote the mention to the other person. A name and then a space
 * is a finished mention. Typing on ("@Ravi K") opens the list again.
 */
export function openMentionAt(
  text: string,
  caret: number,
  targets: readonly MentionTarget[],
): { start: number; query: string } | null {
  const typing = mentionQueryAt(text, caret);
  if (typing === null) return null;
  const written = mentionAt(text, typing.start, targets);
  if (written === null) return typing;
  const end = typing.start + written.length;
  if (end > caret) return null;
  if (caret === end + 1 && text[end] === ' ') return null;
  return typing;
}

/**
 * The draft after taking `target` for the `@...` that starts at `start`.
 *
 * What is replaced is the WORD, not only the part before the caret: picking
 * "Maya Patel" with the caret in "@Ma|ya Pat" must not leave "ya Pat" behind.
 * So the tail that continues the chosen name goes, and then whatever is left of
 * the word it stopped in.
 */
export function completeMention(
  text: string,
  start: number,
  caret: number,
  target: MentionTarget,
): { text: string; caret: number } {
  const name = mentionText(target);
  const typed = text.slice(start + 1, caret);
  let end = caret;
  if (name.toLowerCase().startsWith(typed.toLowerCase())) {
    for (let at = typed.length; at < name.length && end < text.length; at += 1, end += 1) {
      if ((text[end] ?? '').toLowerCase() !== (name[at] ?? '').toLowerCase()) break;
    }
  }
  while (end < text.length && NAME_CHAR.test(text[end] ?? '')) end += 1;

  const head = `${text.slice(0, start)}@${name}`;
  const rest = text.slice(end);
  // The space that ends the mention - the one already there, if there is one.
  return { text: `${head}${rest.startsWith(' ') ? '' : ' '}${rest}`, caret: head.length + 1 };
}

/**
 * Picker order: names that START with the query, then names with a later word
 * (or a username) that does. Once the query holds a space it is a name being
 * finished, so only a whole-name prefix still counts.
 */
export function rankTargets(
  targets: readonly MentionTarget[],
  query: string,
  limit = 8,
): MentionTarget[] {
  const needle = query.toLowerCase();
  if (needle === '') return targets.slice(0, limit);
  const starts: MentionTarget[] = [];
  const later: MentionTarget[] = [];
  for (const target of targets) {
    const label = target.label.toLowerCase();
    if (label.startsWith(needle)) starts.push(target);
    else if (
      !needle.includes(' ') &&
      (label.split(/\s+/).some((word) => word.startsWith(needle)) ||
        (target.alias ?? '').toLowerCase().startsWith(needle))
    ) {
      later.push(target);
    }
  }
  return [...starts, ...later].slice(0, limit);
}

const EVERYONE: MentionTarget = {
  kind: 'all',
  id: null,
  label: 'everyone',
  hint: 'Notify everyone in this chat',
};

/**
 * The people a roster makes mentionable: everybody but you - and, in a group,
 * `@everyone`, as in Teams. (A 1:1 has nobody to call "everyone".)
 */
export function mentionTargetsOf(
  members: readonly ConversationMemberRef[],
  meId: string | null,
): MentionTarget[] {
  const others = members.filter((member) => member.user.id !== meId);
  const bearers = new Map<string, number>();
  for (const member of others) {
    const name = member.user.displayName.toLowerCase();
    bearers.set(name, (bearers.get(name) ?? 0) + 1);
  }
  const people = others.map<MentionTarget>((member) => {
    const { user } = member;
    const shared = (bearers.get(user.displayName.toLowerCase()) ?? 0) > 1;
    return {
      kind: 'user',
      id: user.id,
      label: user.displayName,
      alias: user.username,
      avatarUrl: user.avatarUrl,
      // Two people, one name: the username leads, because it is the only line
      // in the list that says which of them this row is.
      hint: shared
        ? [`@${user.username}`, user.jobTitle].filter(Boolean).join(' - ')
        : (user.jobTitle ?? `@${user.username}`),
      ...(shared ? { shared: true } : {}),
    };
  });
  return people.length >= 2 ? [...people, EVERYONE] : people;
}
